<script lang="ts" setup>
export interface UISheetPageProps {
    /** Brand illustration, e.g. from useThemeIllustration. */
    illustration?: string
    /** Only for an illustration that carries meaning, such as a brand logo; decorative ones stay silent. */
    illustrationAlt?: string
    kicker?: string
    /** Plain text; the title slot takes markup instead (e.g. CMS titles). */
    title?: string
}

const props = defineProps<UISheetPageProps>()

defineSlots<{
    /** In place of the illustration, e.g. a promotion's own image box. */
    media?(): any
    title?(): any
    /** The explanation; several blocks stack with the same gap. */
    default?(): any
    /** The form and buttons. */
    actions?(): any
}>()
</script>

<template>
    <div class="sheet-page">
        <div v-if="$slots.media" class="sheet-page__media">
            <slot name="media" />
        </div>

        <NuxtImg
            v-else-if="props.illustration"
            preset="local"
            provider="cloudflare"
            :src="props.illustration"
            width="96"
            height="96"
            densities="x1 x2"
            :alt="props.illustrationAlt ?? ''"
            :aria-hidden="props.illustrationAlt ? undefined : 'true'"
            class="sheet-page__illustration"
        />

        <div v-if="props.kicker" class="sheet-page__kicker" v-text="props.kicker" />

        <h2 v-if="$slots.title" class="sheet-page__title">
            <slot name="title" />
        </h2>

        <h2 v-else-if="props.title" class="sheet-page__title" v-text="props.title" />

        <div v-if="$slots.default" class="sheet-page__body">
            <slot />
        </div>

        <div v-if="$slots.actions" class="sheet-page__actions">
            <slot name="actions" />
        </div>
    </div>
</template>

<style scoped lang="scss">
// Mirrors the dialog sequence heading (kicker and title), so a page header and the sheet heading look the same.
.sheet-page {
    display: grid;
    color: var(--on-surface, $dialog-font-color);

    // Text follows the sheet's alignment; a grid item doesn't, so the image sits where the dialog sequence puts it.
    &__media {
        justify-self: var(--dialog-sequence-align, start);
        margin-bottom: 10px;
    }

    // Rendered at the size it is requested in (twice that for sharp screens), so it never scales up blurred.
    &__illustration {
        width: 96px;
        height: 96px;
        justify-self: var(--dialog-sequence-align, start);
        margin-bottom: 10px;
        object-fit: contain;
    }

    &__kicker {
        margin-bottom: 8px;
        color: $dialog-text-faint;
        font-size: $font-size-xs;
        font-weight: $text-semi-bold;
        letter-spacing: 0.06em;
        text-transform: uppercase;
    }

    &__title {
        margin: 0;
        font-size: $dialog-title-font-size;
        font-weight: $text-bold;
        line-height: 1.25;
        text-wrap: balance;
    }

    &__body {
        display: grid;
        gap: 8px;
        margin-top: 10px;
        color: $dialog-text-muted;
        font-size: $font-size-sm;
        line-height: 1.6;
    }

    &__actions {
        display: grid;
        gap: 8px;
        margin-top: 24px;
    }

    &__body + &__actions {
        margin-top: 18px;
    }
}
</style>
